<template>
  <div class="relative inline-block w-full sm:w-auto">
    <!-- Botón Nuevo con animación y feedback -->
    <button @click="toggleDropdown" @blur="closeDropdown"
      class="bg-[#2AB4D9] text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 transition-all duration-300 hover:bg-[#25B0D7] hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#25B0D7] w-full sm:w-auto justify-center sm:justify-start"
      :title="buttonLabel">
      <span class="transition-all duration-300" :class="{ 'font-bold': showDropdown }">
        {{ buttonLabel }}
      </span>
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 transition-transform duration-300" :class="{
        'rotate-180': showDropdown
      }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
    </button>

    <!-- Dropdown menú -->
    <Transition name="dropdown">
      <div v-show="showDropdown" class="absolute z-50 mt-2 w-full sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <ul class="py-2">
          <li v-for="option in requestOptions" :key="option.name">
            <button @click="navigateTo(option)"
              class="w-full text-left px-4 py-3 hover:bg-[#f0f9fb] transition-colors duration-150 flex items-center gap-3 group">
              <span class="text-[#2AB4D9] group-hover:scale-110 transition-transform duration-150">
                {{ option.icon }}
              </span>
              <span class="text-gray-700 group-hover:text-[#2AB4D9] transition-colors duration-150 font-medium">
                {{ option.label }}
              </span>
            </button>
          </li>
        </ul>
      </div>
    </Transition>

    <!-- Overlay para cerrar dropdown -->
    <div v-if="showDropdown" @click="closeDropdown" class="fixed inset-0 z-40"></div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { supplyRequestOptions } from '@/services/supplyRequestOptions'

/**
 * Con `supply` (detalle de un CUPS) el menú pasa a ser "Crear solicitud" desde
 * ese suministro: sin alta, y el formulario recibe su id y su energía (ADS-0125).
 */
const props = defineProps({
  supply: { type: Object, default: null },
  label: { type: String, default: '' },
})

const showDropdown = ref(false)
const router = useRouter()
const { t } = useI18n()

const buttonLabel = computed(() => props.label || t('requests.newButton') || 'Nuevo')

// Mismas etiquetas (common.requestTypes) y mismo orden que el panel del cliente:
// Cambio de cuenta penúltimo y Otro al final.
// Retirada «Cambio de comercializadora» (ADS-0130): se deja comentada, no borrada, por si hubiera que reponerla.
// const REQUEST_TYPES = ['supplies', 'invoice', 'change_ownership', 'change_supplier', 'account_change', 'other_type']
const REQUEST_TYPES = ['supplies', 'invoice', 'change_ownership', 'account_change', 'other_type']
const requestOptions = computed(() => {
  if (props.supply) {
    return supplyRequestOptions(t, props.supply).map((option) => ({
      name: option.key,
      label: option.label,
      path: option.key,
      query: option.query,
      icon: '',
    }))
  }
  return REQUEST_TYPES.map((type) => ({
    name: type,
    label: t(`common.requestTypes.${type}`),
    path: `/forms/${type}`,
    icon: '',
  }))
})

function toggleDropdown() {
  showDropdown.value = !showDropdown.value
}

function closeDropdown() {
  showDropdown.value = false
}

function navigateTo(option) {
  closeDropdown()
  router.push(option.query ? { path: option.path, query: option.query } : option.path)
}
</script>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
